<template>
  <div
    v-if="isVisible"
    :class="['codeblock-container', { 'one-line': centerCodeblockActions }]"
    v-bind="shadowCopyAttrs"
    @click="shadowCopy"
  >
    <div
      v-if="helpWithNpmKit"
      class="margin-md margin-horizontal-2xl margin-bottom-0"
    >
      <div
        class="row align-middle align-between with-border-radius padding-vertical-sm"
        style="border-radius: var(--codeblock-border-radius); background-color: var(--gray-700)"
      >
        <div class="column-8">
          <font-awesome-icon
            :icon="$v6.solid.faCircleInfo"
            fixed-width
          />
          Get a
          <site-link
            :to="{ name: 'plans' }"
            class="inverted"
            style="background-color: var(--gray-700)"
            >Pro Plan</site-link
          >, replace <span>KIT_CODE</span> with a Kit token
        </div>
        <div>
          <site-link
            :to="{ name: 'kits' }"
            class="button inverted flat compact margin-bottom-0"
            style="--link-color: var(--white)"
            ><font-awesome-icon
              :icon="$v6.regular.faBackpack"
              fixed-width
            />
            Your Kits</site-link
          >
        </div>
      </div>
    </div>

    <template v-if="codeHidden">
      <div
        ref="codeblock"
        hidden
      >
        <slot />
      </div>
      <div :class="['codeblock', { 'attached-top': attachedTop }]">
        <pre><code><wa-obfuscated-text icon-name="faAsterisk" :length="21" :custom-style="{ color: 'var(--code-color)' }" /></code></pre>
      </div>
    </template>
    <template v-else>
      <div
        v-if="addHighlighting == false"
        ref="codeblock"
        :class="['codeblock', { 'attached-top': attachedTop }]"
      >
        <slot />
      </div>
      <wa-code-highlight
        v-else
        ref="codeblock"
        :lang="lang"
        :code="codeWithReplacements"
        :class="['codeblock', { 'attached-top': attachedTop }]"
      >
        <slot />
      </wa-code-highlight>
    </template>

    <slot name="caption" />
    <div :class="['codeblock-actions', { 'center-y': centerCodeblockActions }]">
      <button
        v-if="toggleCode"
        class="action-button"
        data-balloon-pos="up"
        :aria-label="codeToggleText"
        @click="toggleShowCode"
      >
        <font-awesome-icon
          :icon="$v6.solid[showIcon]"
          fixed-width
          size="lg"
        /><span class="sr-only">{{ codeToggleText }}</span></button
      ><button
        v-if="allowCopy"
        class="action-button"
        :aria-label="`${copyText}`"
        data-balloon-pos="up"
        @click="copy"
      >
        <font-awesome-icon
          :icon="$v6.solid[copyIcon]"
          fixed-width
          size="lg"
        /><span class="sr-only">{{ copyText }}</span>
      </button>
    </div>
  </div>
</template>

<script>
  export default {
    name: 'CodeBlock',

    props: {
      lang: {
        type: String,
        default: 'markup'
      },

      use: {
        type: String,
        default: 'webfonts'
      },

      allowCopy: {
        type: Boolean,
        default: false
      },

      allowShadowCopy: {
        type: Boolean,
        default: false
      },

      name: {
        type: String,
        default: 'default'
      },

      customCopyIcon: {
        type: String,
        default: 'faCopy'
      },

      copyText: {
        type: String,
        default: 'Copy Code Snippet'
      },

      copyTrackingEvent: {
        type: String,
        default: null
      },

      copyTrackingParams: {
        type: Object,
        default: null
      },

      centerCodeblockActions: {
        type: Boolean,
        default: false
      },

      attachedTop: {
        type: Boolean,
        default: false
      },

      code: {
        type: String
      },

      centerIcons: {
        type: Boolean,
        default: false
      },

      replace: {
        type: Object,
        default() {
          return {}
        }
      },

      addHighlighting: {
        type: Boolean,
        default: false
      },

      hideCode: {
        type: Boolean,
        default: false
      },

      showCodeLabel: {
        type: String,
        default: 'Show Code'
      },

      hideCodeLabel: {
        type: String,
        default: 'Hide Code'
      },

      toggleCode: {
        type: Boolean,
        default: false
      },

      helpWithNpmKit: {
        type: Boolean,
        default: false
      }
    },

    data() {
      return {
        isVisible: true,
        isCopying: false,
        shouldHideCode: null
      }
    },

    computed: {
      copyIcon() {
        if (this.isCopying) {
          return 'faCheck'
        } else {
          return this.customCopyIcon ? this.customCopyIcon : 'faCopy'
        }
      },

      showIcon() {
        if (this.shouldHideCode) {
          return 'faEye'
        } else {
          return 'faEyeSlash'
        }
      },

      codeWithReplacements() {
        const replaceKeys = Object.keys(this.replace)

        return replaceKeys.reduce((acc, k) => {
          return acc.replaceAll(k, this.replace[k])
        }, this.code)
      },

      codeHidden() {
        return this.shouldHideCode
      },

      codeToggleText() {
        if (this.shouldHideCode) {
          return this.showCodeLabel
        } else {
          return this.hideCodeLabel
        }
      },

      shadowCopyAttrs() {
        if (this.allowShadowCopy) {
          return {
            'aria-label': this.isCopying ? 'Copied' : this.copyText,
            'data-balloon-pos': 'up'
          }
        } else {
          return {}
        }
      }
    },

    mounted() {
      this.doReplacements()
      this.shouldHideCode = this.hideCode
    },

    updated() {
      this.doReplacements()
    },

    methods: {
      doReplacements() {
        let codeblock = this.$refs.codeblock

        // We don't do the replacements if we are using this.addHighlighting because that indicates that we are using the <wa-code-highlighting> component.
        // <wa-code-highlighting> requires that we perform the replacements before we bind the "code" property.
        if (!codeblock || this.addHighlighting) return

        const tw = document.createTreeWalker(codeblock, NodeFilter.SHOW_TEXT)
        const textNodes = []

        while (tw.nextNode()) {
          textNodes.push(tw.currentNode)
        }

        const replaceKeys = Object.keys(this.replace)

        textNodes.forEach((child) => {
          replaceKeys.forEach((k) => {
            child.nodeValue = child.nodeValue.replaceAll(k, this.replace[k])
          })
        })
      },

      copy(event) {
        if (event) {
          event.preventDefault()
        }

        if (this.addHighlighting) {
          this.$clipboard(this.$refs.codeblock.code)
        } else {
          this.$clipboard(this.$refs.codeblock.innerText)
        }

        if (this.copyTrackingEvent !== null) {
          this.$posthog.capture(this.copyTrackingEvent, this.copyTrackingParams)
        }

        this.isCopying = true

        setTimeout(() => {
          this.isCopying = false
        }, 1000)
      },

      shadowCopy() {
        if (this.allowShadowCopy) {
          this.copy()
        }
      },

      toggleShowCode(event) {
        event.preventDefault()

        this.shouldHideCode = !this.shouldHideCode
      }
    }
  }
</script>
